import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { focusTokens } from '../../../styles/theme/focus';
import type { SegmentedControlProps } from './SegmentedControl.types';

/** Same teal as product drawer step "Precios" (`tealMedium`). */
const TEAL = '#14b8a6';

/**
 * Modern segmented control: no track — only the selected option shows a teal pill.
 * `outlined` outlines every option with the same green as text inputs (`focusTokens`), so the
 * choice reads like the fields around it.
 * Supports an unset (`null`) value and a compact error outline.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  fullWidth = true,
  disabled = false,
  size = 'small',
  error = false,
  variant = 'pill',
}: SegmentedControlProps<T>) {
  const isSmall = size === 'small';
  const isOutlined = variant === 'outlined';

  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      aria-invalid={error || undefined}
      sx={(theme) => ({
        display: 'inline-flex',
        width: fullWidth ? '100%' : 'auto',
        gap: 0.5,
        p: error ? 0.5 : 0,
        borderRadius: error ? 2 : 0,
        backgroundColor: error
          ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
          : 'transparent',
        border: error
          ? `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.55 : 0.45)}`
          : 'none',
        transition: theme.transitions.create(['background-color', 'border-color', 'padding'], {
          duration: 160,
        }),
      })}
    >
      {options.map((option) => {
        const selected = value != null && option.value === value;
        return (
          <ButtonBase
            key={option.value}
            disableRipple
            disabled={disabled}
            role="radio"
            aria-checked={selected}
            aria-label={option.ariaLabel ?? option.label}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            sx={(theme) => {
              const isDark = theme.palette.mode === 'dark';
              const selectedColor = isDark
                ? theme.palette.success.light
                : isOutlined
                  ? theme.palette.success.dark
                  : TEAL;
              const outlinedTint = theme.palette.success.main;
              return {
                flex: fullWidth ? 1 : undefined,
                minWidth: fullWidth ? 0 : undefined,
                px: isSmall ? 1.25 : 1.75,
                py: isSmall ? 0.7 : 0.9,
                borderRadius: isOutlined ? '8px' : 999,
                textTransform: 'none',
                fontWeight: isOutlined && selected ? 700 : 600,
                fontSize: isSmall ? '0.8125rem' : '0.875rem',
                lineHeight: 1.25,
                whiteSpace: 'normal',
                textAlign: 'center',
                color: selected
                  ? selectedColor
                  : isOutlined
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary,
                backgroundColor: selected
                  ? isOutlined
                    ? alpha(outlinedTint, isDark ? 0.15 : 0.12)
                    : alpha(TEAL, isDark ? 0.15 : 0.1)
                  : 'transparent',
                // Outlined: idle/selected borders match a text input idle/focused. The selected
                // one adds an inner ring (box-shadow) so it looks 2px without shifting the layout.
                border: isOutlined
                  ? `${focusTokens.idleBorderWidth}px solid ${
                      selected ? focusTokens.borderColor : focusTokens.idleBorderColor
                    }`
                  : 'none',
                boxShadow: selected
                  ? isOutlined
                    ? `inset 0 0 0 ${focusTokens.borderWidth - focusTokens.idleBorderWidth}px ${
                        focusTokens.borderColor
                      }`
                    : `inset 0 0 0 1px ${alpha(TEAL, isDark ? 0.3 : 0.2)}`
                  : 'none',
                transition: theme.transitions.create(
                  ['background-color', 'color', 'box-shadow', 'border-color'],
                  { duration: 160 },
                ),
                '&:hover': {
                  backgroundColor: selected
                    ? isOutlined
                      ? alpha(outlinedTint, isDark ? 0.22 : 0.16)
                      : alpha(TEAL, isDark ? 0.22 : 0.14)
                    : isOutlined
                      ? alpha(outlinedTint, isDark ? 0.1 : 0.06)
                      : isDark
                        ? alpha(theme.palette.common.white, 0.06)
                        : alpha(theme.palette.common.black, 0.04),
                  ...(isOutlined && !selected ? { borderColor: focusTokens.hoverBorderColor } : {}),
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              };
            }}
          >
            <Typography
              component="span"
              variant="inherit"
              sx={{
                fontWeight: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
